import { defineFeature } from '../define-feature';
import { TEST_IDS, sidebarNavTestId } from '../test-ids';

export default defineFeature({
  id: 'agenda.booking.share',
  category: 'agenda',
  title: 'Partager le lien de réservation en ligne',
  intent:
    'Diffuser votre page de réservation en ligne : partager un lien (global ou ciblé par prestation/lieu/membre), personnaliser son apparence, ou intégrer le tunnel de réservation directement sur votre propre site ou une landing page, sans coder.',
  context: {
    why: "Le lien de réservation est votre canal principal de captation de rendez-vous en ligne — c'est la porte d'entrée que vous ouvrez à vos clients potentiels sur tous les endroits où ils vous cherchent. Il peut être diffusé tel quel dans votre bio Instagram, votre fiche Google, votre signature d'email, ou intégré directement sur votre propre site web ou une landing page dédiée. Cette double capacité — lien à partager et widget à intégrer — est ce qui fait sa puissance. Trois usages majeurs se dégagent. Le premier est le partage brut : vous collez le lien dans un post, une bio ou un SMS, et le client tombe sur votre page de réservation Propelsia. Le deuxième est le ciblage : vous générez un lien qui ne propose qu'une prestation précise (l'appel découverte), qu'un membre précis (votre spécialiste) ou qu'un lieu précis (votre boutique du centre-ville) — utile pour des campagnes ou des landing pages spécifiques. Le troisième est l'intégration technique : vous collez deux bouts de code sur votre propre site et le tunnel de réservation s'affiche directement dans votre page, sans que le client ne quitte l'URL de votre site — un avantage majeur pour préserver votre SEO et donner une impression de continuité. Point rassurant : tout cela se fait sans coder. Il suffit de suivre les étapes proposées par l'interface et de copier-coller les codes fournis, y compris sur des sites WordPress, Wix ou custom.",
    use_cases: [
      'Vous voulez un lien global à mettre partout où vous existez en ligne : bio Instagram, fiche Google Business Profile, signature de vos e-mails, publications réseaux sociaux.',
      'Vous préparez une campagne pour un appel découverte gratuit : générez un lien ciblé qui ne propose que cette prestation-là pour éviter que le prospect ne se perde dans le catalogue.',
      "Vous voulez qu'un lien mène directement chez une personne spécifique de votre équipe (votre spécialiste balayage, votre kiné du sport) : configurez le ciblage sur ce membre.",
      "Vous avez une landing page de vente qui doit se convertir en réservation directe : intégrez le tunnel dans la page pour que le client ne quitte jamais l'URL, ce qui est excellent pour le SEO.",
      "Vous voulez ajouter un bouton « Prendre RDV » sur votre site qui ouvre la réservation en modale (popup) sans changer de page : utilisez le mode d'intégration bouton + modale.",
      "Vous êtes sur WordPress, Wix ou un site custom et vous n'avez pas de développeur sous la main : le widget se colle en deux morceaux de code standardisés, sans compétence technique requise.",
    ],
  },
  required_scopes: ['establishment.widget_domain.create', 'establishment.widget_domain.delete'],
  prerequisites: [
    {
      ref: 'agenda.availability.manage',
      requirement: 'required',
      why: "Sans disponibilités, la page de réservation n'affiche aucun créneau.",
    },
    {
      ref: 'catalog.service.manage',
      requirement: 'recommended',
      why: 'Des prestations réservables rendent la page utile.',
    },
    {
      ref: 'agenda.booking.settings',
      requirement: 'recommended',
      why: 'Les règles de réservation s’appliquent à la page partagée.',
    },
  ],
  ui_entry_points: [
    { route: '/agenda', element: 'Icône « Partager » → « Partager votre réservation en ligne »' },
    {
      route: '/agenda',
      element:
        'Clic sur une disponibilité → « Partager le calendrier » → « Ouvrir la page de réservation » / « Partager »',
    },
  ],
  api: {
    primary_operation_id: 'WidgetDomainAppController_create',
    related_operation_ids: ['WidgetDomainAppController_list', 'WidgetDomainAppController_remove'],
  },
  steps: [
    "**Ouvrez le partage** : dans l'agenda, cliquez sur l'icône « Partager » (« Partager votre réservation en ligne »).",
    '**Ciblez le lien (optionnel)** : sous « Créer un lien », filtrez par Prestations, Lieux et Membres — laissez vide pour tout proposer.',
    "**Apparence (optionnel)** : gardez « Apparence par défaut » ou cochez « Personnaliser l'apparence pour ce lien ».",
    "**Onglet « Lien »** : copiez le « Lien à partager » (ex. `https://app.propelsia.ai/book/…`). Vous pouvez aussi l'Ouvrir ou le Réinitialiser.",
    "**Onglet « Code d'intégration »** (pour un site) : choisissez le mode, copiez les deux morceaux de code (widget + script), collez-les sur votre site, puis testez la page.",
  ],
  gotchas: [
    '**Lien vide = tout proposé** : sans filtre (prestations/lieux/membres), le lien propose toute votre offre.',
    "**Depuis une disponibilité** : cliquer un créneau de l'agenda puis « Partager » pré-remplit le lien avec le périmètre de ce planning (ses prestations, lieux et membres). « Selon la disponibilité » rétablit ce périmètre ; « Aucune restriction » enlève tous les filtres.",
    "**Planning réservé à certains tags** : le lien reste utilisable, mais un client non tagué parcourra le tunnel et sera refusé à la confirmation — à n'envoyer qu'aux clients concernés.",
    "**SEO préservé** : le tunnel s'affiche DANS la page sans changer l'URL du site → vous gardez le référencement de votre propre page ou landing page.",
    '**Sans code** : il suffit de coller 2 bouts de code (widget + script) et de suivre les étapes — aucun développement nécessaire.',
    "**Script une seule fois** : si vous avez plusieurs widgets Propelsia sur la même page, le script `booking.js` ne s'ajoute QU'UNE fois.",
    "**Deux modes d'intégration** : tunnel intégré dans la page (inline) OU bouton qui ouvre une modale (popup). À choisir selon le rendu voulu.",
    '**Widget code** : (1) `<propelsia-booking widget-id="…"></propelsia-booking>` à l\'emplacement voulu ; (2) `<script src="https://widget.propelsia.ai/booking.js" defer></script>` une seule fois dans la page. Fonctionne sur WordPress, Wix ou site custom.',
    '**Temps réel** : la page reflète en direct vos disponibilités, prestations et paramètres de réservation. Aucune synchronisation à déclencher manuellement.',
    '**Lien ciblé pratique** : un lien filtré sur « appel découverte » posé dans la bio Instagram ou une landing page évite au client de se perdre dans le catalogue complet.',
    "**Gating éventuel** : l'intégration widget peut dépendre de votre plan d'abonnement (offre Booster — à confirmer côté produit).",
  ],
  related_features: ['agenda.availability.manage', 'agenda.booking.settings', 'catalog.service.manage'],
  demo_script: {
    // Deep-link direct vers la modale de partage, onglet « Lien » par défaut.
    duration_target_s: 25,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption: 'Le lien de réservation est votre porte d’entrée : à poser partout où vos clients vous cherchent.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/agenda'),
        caption: "Direction l'Agenda, dans le menu latéral.",
      },
      {
        action: 'click',
        target: TEST_IDS.agenda_share_button,
        caption: "En haut de l'agenda, l'icône « Partager » ouvre votre réservation en ligne.",
      },
      {
        action: 'expect_visible',
        target: TEST_IDS.agenda_share_link_input,
        caption: 'Voici votre lien de réservation, prêt à être diffusé tel quel.',
        capture: true,
      },
      {
        action: 'click',
        target: TEST_IDS.agenda_share_copy,
        caption: 'Un clic pour le copier, puis collez-le dans votre bio, votre fiche Google ou vos e-mails.',
        capture: true,
      },
    ],
  },
});

// Verbatim Kaltouma (à conserver) :
// > Partager le lien de réservation en ligne : soit lien direct, soit
// > procédure pour intégrer le tunnel de réservation sur un site existant.
// > Créer un lien : filtres Prestations / Lieux / Membres (vide = tout),
// > apparence personnalisée par lien. Onglet Lien : lien à partager (Copier
// > / Ouvrir / Réinitialiser). Onglet Code d'intégration : 2 morceaux à
// > coller (WordPress, Wix, custom) — 2 modes (Tunnel intégré / Bouton
// > modale), widget <propelsia-booking> + script booking.js une seule fois,
// > puis Tester. Avantage clé : SANS quitter l'URL du site → préserve le
// > SEO.
